import React from 'react';
import type { AiAdvice, AiManager } from '../types/meeting';

interface AiAdviceListProps {
  aiAdviceHistory: AiAdvice[];
  aiManagers: AiManager[];
  onShowHistory: () => void;
}

// OKLCH色生成関数（IDベースでユニークな色を生成）
const getManagerColor = (id: number): string => {
  const hue = (id * 137.508) % 360; // 黄金角による分散
  return `oklch(0.45 0.15 ${hue})`; // L=0.45で暗め、C=0.15で落ち着いた彩度
};

// 相対時間を計算
const formatRelativeTime = (timestamp: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - timestamp.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'たった今';
  if (diffHours < 1) return `${diffMinutes}分前`;
  if (diffDays < 1) return `${diffHours}時間前`;
  return `${diffDays}日前`;
};

// テキストの改行を<br>タグに変換
const formatAdviceText = (text: string): string => {
  if (!text) return '';
  return text.replace(/\r\n/g, '<br>').replace(/\n/g, '<br>');
};

const AiAdviceList: React.FC<AiAdviceListProps> = ({
  aiAdviceHistory,
  aiManagers,
  onShowHistory,
}) => {
  const getManager = (managerId: number): AiManager | undefined => {
    return aiManagers.find((m) => m.id === managerId);
  };

  // 最新5件のみ表示
  const displayAdvices = aiAdviceHistory.slice(0, 5);

  return (
    <div className="panel-section" id="ai-manager-section">
      <div className="section-title">
        <span className="section-icon material-symbols-outlined">psychology</span>
        AIマネージャー
        <div className="section-action material-symbols-outlined" onClick={onShowHistory}>
          history
        </div>
      </div>
      <div className="ai-advice-container">
        {displayAdvices.length === 0 ? (
          <div className="empty-state">
            <span className="material-symbols-outlined empty-state-icon">psychology</span>
            <div className="empty-state-text">
              AIマネージャーが会議を分析し、リアルタイムでアドバイスを提供します。
            </div>
          </div>
        ) : (
          displayAdvices.map((advice, index) => {
            const manager = getManager(advice.managerId);
            const managerName = advice.managerName || manager?.name || 'AIマネージャー';
            const managerColor = getManagerColor(advice.managerId);

            return (
              <div
                key={`${advice.managerId}-${index}`}
                className="ai-advice-bubble"
                onClick={onShowHistory}
              >
                <div
                  className="ai-advice-avatar"
                  style={{ backgroundColor: managerColor }}
                >
                  <span className="material-symbols-outlined">smart_toy</span>
                </div>
                <div className="ai-advice-content">
                  <div
                    className="ai-advice-text"
                    dangerouslySetInnerHTML={{ __html: formatAdviceText(advice.text) }}
                  />
                  <div className="ai-advice-meta">
                    <span className="ai-advice-name">{managerName}</span>
                    <span className="ai-advice-time">
                      {formatRelativeTime(advice.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AiAdviceList;
